// Captura errores de render para que un fallo NO deje la pantalla en blanco: muestra el error real
// (con botón para recargar) y aísla la parte que falla del resto de la app.
import { Component } from 'react'
import { Ico } from './ui'
import { t, tx } from '../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

// ── B-867 · HAY UNA VERSIÓN NUEVA Y ESTA PESTAÑA TIENE LA VIEJA ──────────────────────────────────
// El dueño (25-sep-2026): «En ocasiones me salen errores raros, ahora he abierto una radiografia y dice, esta parte ha
// fallado /pacientes failed to fetch dynamically imported module .../assets/DicomViewer-CwiDyGD.js». Cada push se
// despliega al momento; quien tiene la app abierta sigue con el index.html de antes, y al abrir una parte que aún no
// había cargado (el visor de radiografías, el cobro, el escáner...) la pide por su nombre viejo. El servidor ya guarda
// unos días los trozos de la versión anterior (backend/routes/deploy.js · publicarFrontend); esto es la red de debajo:
// si aun así falta uno, la página se RECARGA SOLA una vez y trae la versión nueva, en vez de enseñar un error técnico.
// Cada navegador lo dice a su manera (Chrome, Firefox, Safari) y Vite avisa antes con su evento 'vite:preloadError'.
export const esFalloDeCarga = (e) => /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|Loading (CSS )?chunk|ChunkLoadError/i
  .test(String((e && (e.message || e.name)) || e || ''))

// Una vez, y no en bucle: si ya se recargó hace menos de un minuto por lo mismo, el fallo no es la versión (sería el
// servidor o la red) y se deja ver con su botón. Sin conexión tampoco: recargar solo cambiaría el error por la página
// de «sin internet» del navegador. Sin almacenamiento de la pestaña no hay forma de saber si ya se recargó: no se
// arriesga un bucle.
const CLAVE_RECARGA = 'biango:recarga-por-version'
export function recargarPorVersionNueva(ventana = typeof window !== 'undefined' ? window : null) {
  if (!ventana || !ventana.location) return false
  if (ventana.navigator && ventana.navigator.onLine === false) return false
  try {
    const antes = Number(ventana.sessionStorage.getItem(CLAVE_RECARGA) || 0)
    if (Date.now() - antes < 60000) return false
    ventana.sessionStorage.setItem(CLAVE_RECARGA, String(Date.now()))
  } catch (_) { return false }
  ventana.location.reload()
  return true
}

// Lo engancha main.jsx al arrancar: Vite avisa con 'vite:preloadError' de CUALQUIER importación perezosa que falla (las de
// React.lazy y las `await import()`), también de las que la pantalla captura y enseñaría como un error suyo.
export function vigilarVersionNueva(ventana = typeof window !== 'undefined' ? window : null) {
  if (!ventana || typeof ventana.addEventListener !== 'function') return
  ventana.addEventListener('vite:preloadError', (ev) => {
    if (recargarPorVersionNueva(ventana) && ev && typeof ev.preventDefault === 'function') ev.preventDefault()
  })
}

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { error: null }
  }
  static getDerivedStateFromError(error) {
    return { error }
  }
  componentDidCatch(error, info) {
    console.error('[ErrorBoundary]', this.props.label || '', error, info?.componentStack)
    if (esFalloDeCarga(error)) recargarPorVersionNueva()
  }
  render() {
    if (this.state.error && esFalloDeCarga(this.state.error)) {
      return (
        <div data-version-nueva="" style={{ padding: '16px', margin: '8px 0', border: '1px solid var(--border, #d1d5db)', borderRadius: '10px', background: 'var(--surface-2, #f8fafc)', color: 'var(--text, #1f2937)', fontSize: '13px' }}>
          <div style={{ fontWeight: 800, marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}><Ico n="info" s={16} />{t('components.ErrorBoundary.hay_una_version_nueva_de', 'Hay una versión nueva de la aplicación')}</div>
          <div style={{ marginBottom: '10px', color: 'var(--text-secondary, #475569)', lineHeight: 1.5 }}>{t('components.ErrorBoundary.se_esta_recargando_la_pagina', 'Se está recargando la página para traerla. Si no se recarga sola, pulsa «Recargar página». Lo que ya estaba guardado no se pierde.')}</div>
          <button onClick={() => window.location.reload()} style={{ padding: '7px 12px', border: 'none', borderRadius: '8px', background: 'var(--primary, #1E88E5)', color: 'var(--sobre-primary)', fontSize: '12.5px', fontWeight: 700, cursor: 'pointer' }}>{t('components.ErrorBoundary.recargar_pagina', 'Recargar página')}</button>
        </div>
      )
    }
    if (this.state.error) {
      const msg = this.state.error?.message || String(this.state.error)
      return (
        <div style={{ padding: '16px', margin: '8px 0', border: '1px solid var(--danger, #dc2626)', borderRadius: '10px', background: 'var(--danger-bg, #fef2f2)', color: 'var(--danger, #991b1b)', fontSize: '13px' }}>
          <div style={{ fontWeight: 800, marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}><Ico n="warn" s={16} />{tx('components.ErrorBoundary.esta_parte_ha_fallado', 'Esta parte ha fallado{{x}}', { x: this.props.label ? ` (${this.props.label})` : '' })}</div>
          <div style={{ fontFamily: 'monospace', fontSize: '12px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginBottom: '10px', color: 'var(--text, #1f2937)' }}>{msg}</div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => this.setState({ error: null })} style={{ padding: '7px 12px', border: '1px solid var(--border, #d1d5db)', borderRadius: '8px', background: 'var(--surface, #fff)', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer' }}>{t('components.ErrorBoundary.reintentar', 'Reintentar')}</button>
            <button onClick={() => window.location.reload()} style={{ padding: '7px 12px', border: 'none', borderRadius: '8px', background: 'var(--primary, #1E88E5)', color: 'var(--sobre-primary)', fontSize: '12.5px', fontWeight: 700, cursor: 'pointer' }}>{t('components.ErrorBoundary.recargar_pagina', 'Recargar página')}</button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
